import { useCallback, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer, type View } from "react-big-calendar";
import { useSearchParams } from "react-router-dom";
import { format, parse, startOfWeek, getDay, addDays, addHours } from "date-fns";
import { it } from "date-fns/locale/it";

import { useEventsQuery } from "@/hooks/useEventsQuery";
import { useCalendarFilters } from "@/hooks/useCalendarFilters";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  getCalendarRange,
  parseDateParam,
  formatDateParam,
  type CalendarView,
} from "@/lib/calendarRange";
import { getCategoryColors } from "@/lib/outlookCategoryColors";
import { EventListQuerySchema, type EventListItem, type EventListQuery, type ProcessingStatus } from "@cdt/shared";
import type { CalendarEvent } from "@/components/calendar/types";
import { CalendarEventChip } from "@/components/calendar/CalendarEventChip";
import { CalendarEventDetailSheet } from "@/components/calendar/CalendarEventDetailSheet";
import { CalendarFilterBar } from "@/components/calendar/CalendarFilterBar";

export type { CalendarEvent };

const locales = { it };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const ITALIAN_MESSAGES = {
  date: "Data",
  time: "Ora",
  event: "Evento",
  allDay: "Tutto il giorno",
  week: "Settimana",
  day: "Giorno",
  month: "Mese",
  previous: "Precedente",
  next: "Successivo",
  today: "Oggi",
  agenda: "Agenda",
  noEventsInRange: "Nessun evento nel periodo selezionato.",
  showMore: (count: number) => `+${count} altri`,
};

/** Scroll week/day view to the top so midnight events are visible. */
const SCROLL_TO_TIME = new Date(1970, 0, 1, 0, 0, 0);

/**
 * CalendarService adds 2 h when endDate is null (see calendarService.ts).
 * We mirror the same fallback on the frontend for timed events.
 */
const DEFAULT_TIMED_DURATION_H = 2;

const DEFAULT_VIEW_DESKTOP: CalendarView = "month";
const DEFAULT_VIEW_MOBILE: CalendarView = "agenda";

const CALENDAR_COMPONENTS = { event: CalendarEventChip };

/** Base (non-filter) query fields that are always fixed for the calendar. */
const CALENDAR_BASE = EventListQuerySchema.parse({
  pageSize: 100,
  sortBy: "startDate",
  sortOrder: "asc",
});

export function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const { filters, setPatch, clearAll, isDefault } = useCalendarFilters();

  const defaultView = isMobile ? DEFAULT_VIEW_MOBILE : DEFAULT_VIEW_DESKTOP;

  const rawView = searchParams.get("view") as CalendarView | null;
  const view: CalendarView =
    rawView === "month" || rawView === "week" || rawView === "day" || rawView === "agenda"
      ? rawView
      : defaultView;

  // Stabilise the Date object so useMemo doesn't recompute on every render
  // when there is no `date` URL param (parseDateParam would return new Date()
  // which is a different reference each time).
  const rawDateParam = searchParams.get("date");
  const dateRef = useRef<Date>(parseDateParam(rawDateParam));
  const date = useMemo(() => {
    const parsed = parseDateParam(rawDateParam);
    // Only update the ref when the calendar date actually changes (different day).
    if (formatDateParam(parsed) !== formatDateParam(dateRef.current)) {
      dateRef.current = parsed;
    }
    return dateRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDateParam]);

  const range = useMemo(() => getCalendarRange(view, date), [view, date]);

  const eventsQuery = useMemo(
    (): EventListQuery => ({
      ...CALENDAR_BASE,
      status: ["CREATED"] as ProcessingStatus[],
      categoryId: filters.categoryId,
      uncategorized: filters.uncategorized === true ? true : undefined,
      priority: filters.priority !== undefined ? [filters.priority] : undefined,
      hideMultiDay: filters.hideMultiDay === true ? true : undefined,
      startDateFrom: range.from,
      startDateTo: range.to,
    }),
    [filters, range],
  );

  const { data, isLoading, isError } = useEventsQuery(eventsQuery);

  const events = useMemo<CalendarEvent[]>(() => {
    if (!data) return [];
    return data.items
      .filter((item): item is EventListItem & { startDate: string } =>
        item.startDate !== null,
      )
      .map((item) => {
        const start = new Date(item.startDate);

        // Treat as all-day when the extracted time is midnight local time.
        // This is the common case when the AI extracts only a date without
        // a specific hour (e.g. "Conference on April 28").
        const isAllDay =
          start.getHours() === 0 &&
          start.getMinutes() === 0 &&
          start.getSeconds() === 0 &&
          start.getMilliseconds() === 0;

        let end: Date;
        if (item.endDate) {
          end = new Date(item.endDate);
          // All-day events: react-big-calendar uses end as EXCLUSIVE (start of
          // next day). If the backend stored an inclusive midnight end date on
          // the same day as start, bump it forward one day.
          if (isAllDay && end.getTime() <= start.getTime()) {
            end = addDays(start, 1);
          }
        } else if (isAllDay) {
          // No endDate and all-day: span exactly 1 day (end = next midnight).
          end = addDays(start, 1);
        } else {
          // No endDate and timed event: mirror the 2-hour default used by
          // CalendarService when creating Outlook events.
          end = addHours(start, DEFAULT_TIMED_DURATION_H);
        }

        return {
          id: item.id,
          title: item.title ?? item.emailSubject,
          start,
          end,
          allDay: isAllDay,
          resource: item,
        };
      });
  }, [data]);

  const handleView = useCallback(
    (newView: View) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", newView as CalendarView);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("date", formatDateParam(newDate));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedEventId(event.id);
  }, []);

  const eventPropGetter = useCallback((event: CalendarEvent) => {
    const colors = getCategoryColors(event.resource.category?.outlookColor);
    return {
      style: {
        background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.border} 130%)`,
        color: colors.foreground,
        border: "none",
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: "8px",
        padding: "2px 8px",
        boxShadow:
          "0 1px 2px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.10)",
        fontWeight: 600,
      },
    };
  }, []);

  return (
    <div className="flex h-full flex-col gap-3 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Calendario</h1>
        {isLoading && (
          <span className="text-sm text-muted-foreground">Caricamento…</span>
        )}
        {isError && (
          <span className="text-sm text-destructive">
            Errore nel caricamento degli eventi
          </span>
        )}
      </div>

      <CalendarFilterBar
        filters={filters}
        onChange={setPatch}
        onClear={clearAll}
        isDefault={isDefault}
      />

      <div className="min-h-0 flex-1 rounded-lg border bg-card p-2">
        <Calendar<CalendarEvent>
          localizer={localizer}
          culture="it"
          messages={ITALIAN_MESSAGES}
          events={events}
          view={view}
          date={date}
          onView={handleView}
          onNavigate={handleNavigate}
          onSelectEvent={handleSelectEvent}
          views={["month", "week", "day", "agenda"]}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          style={{ height: "100%" }}
          scrollToTime={SCROLL_TO_TIME}
          popup
          components={CALENDAR_COMPONENTS}
          eventPropGetter={eventPropGetter}
        />
      </div>

      <CalendarEventDetailSheet
        eventId={selectedEventId}
        onClose={() => setSelectedEventId(null)}
      />
    </div>
  );
}
